import {
  CloseOutlined,
  FileSearchOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Col, Divider, Form, Input, Modal, Row } from "antd";
import DataTable from "components/elements/DataTable";
import { ColumnType } from "components/elements/DataTable/DataTable";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyFindTreuhandstelleQuery } from "store/masterdata";
import {
  FindTreuhandstelleParams,
  Treuhandstelle,
} from "types/masterdata.types";
import SearchTHModal from "./SearchTHModal";
import styles from "../ChangeTHsModal.module.less";
import CreateNewTHsForm from "../Forms/CreateNewTHsForm";

interface Props {
  kundennummer: number;
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
}

function SearchTHs({
  kundennummer,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [selectedTHs, setSelectedTHs] = useState<number | null>(null);
  const [searchTHModalVisible, setSearchTHModalVisible] = useState(false);
  const [createNewTHsFormModalVisible, setCreateNewTHsFormModalVisible] =
    useState(false);
  const [findTreuhandstelle, { data, isLoading, isFetching }] =
    useLazyFindTreuhandstelleQuery();

  const onSearch = (values: FindTreuhandstelleParams) => {
    setSelectedTHs(null);
    findTreuhandstelle(values);
  };

  const openSearchTHModal = () => {
    setSearchTHModalVisible(true);
  };

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.search_ths")}
      visible={visible}
      onCancel={onClose}
      width={1000}
      destroyOnClose
    >
      <Form
        id="landwirt-form"
        form={form}
        onFinish={onSearch}
        layout="vertical"
        initialValues={{
          name: "",
          address: "",
          postalCode: "",
          place: "",
        }}
      >
        <Row gutter={10}>
          <Col span={5}>
            <Form.Item name="name">
              <Input placeholder={t("masterdata.lw_details.ths_name")} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="address">
              <Input placeholder={t("masterdata.lw_details.address")} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="postalCode">
              <Input placeholder={t("masterdata.lw_details.postal_code")} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="place">
              <Input placeholder={t("masterdata.lw_details.ort")} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              htmlType="submit"
              block
            >
              {t("search")}
            </Button>
          </Col>
        </Row>
      </Form>
      <div className={styles.table_container}>
        <DataTable<Treuhandstelle>
          isLoading={isLoading || isFetching}
          actions={[]}
          headers={[
            {
              title: t("masterdata.lw_details.ths_name"),
              key: "name",
              type: ColumnType.Text,
              width: 300,
            },
            {
              title: t("masterdata.lw_details.address"),
              key: "address",
              type: ColumnType.Text,
              width: 200,
            },
            {
              title: t("masterdata.lw_details.postal_code"),
              key: "postalCode",
              type: ColumnType.Text,
              width: 100,
            },
            {
              title: t("masterdata.lw_details.ort"),
              key: "place",
              type: ColumnType.Text,
              width: 100,
            },
          ]}
          data={data}
          defaultPageSize={10}
          rowSelection={{
            type: "radio",
            onChange: (
              _selectedRowKeys: React.Key[],
              selectedRows: Treuhandstelle[]
            ) => {
              const selectedRow = selectedRows[0];
              setSelectedTHs(selectedRow.id);
            },
          }}
          stickyHeader
          withTitle={false}
        />
      </div>
      <Divider />
      <Row gutter={[10, 10]}>
        <Col span={6}>
          <Button
            icon={<CloseOutlined />}
            onClick={onClose}
            type="dashed"
            block
          >
            {t("abort")}
          </Button>
        </Col>
        <Col span={9}>
          <Button
            onClick={() => setCreateNewTHsFormModalVisible(true)}
            icon={<PlusOutlined />}
            type="default"
            block
          >
            {t("masterdata.lw_details.register_new_ths")}
          </Button>
        </Col>
        <Col span={9}>
          <Button
            disabled={!selectedTHs}
            icon={<FileSearchOutlined />}
            type="primary"
            onClick={openSearchTHModal}
            block
          >
            {t("masterdata.lw_details.search_th_for_ths")}
          </Button>
        </Col>
      </Row>
      {searchTHModalVisible && selectedTHs && (
        <SearchTHModal
          kundennummer={kundennummer}
          selectedTHs={selectedTHs}
          visible={searchTHModalVisible}
          onClose={() => setSearchTHModalVisible(false)}
          onSuccess={() => {
            onClose();
            onSuccess();
          }}
        />
      )}
      <CreateNewTHsForm
        kundennummer={kundennummer}
        onClose={() => setCreateNewTHsFormModalVisible(false)}
        onSuccess={findTreuhandstelle}
        visible={createNewTHsFormModalVisible}
      />
    </Modal>
  );
}

export default SearchTHs;
